import { useContext, useEffect, useState } from "react";
import { Form, Row, Col, Container } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import InputText from "../inputText/InputText";
import CustomButton from "../customButton/CustomButton";
import { Link, useNavigate } from "react-router-dom";
import ReactNotifications, { showApiErrors } from "../reactNotifications/ReactNotifications";
import * as utilConstants from "../../../utils/constants/utilsConstants";
import Cookies from 'js-cookie';
import { jwtDecode } from "jwt-decode";
import SignupLeftSideCard from "../cards/SignupLeftSideCard";
import Stepper from "./Stepper";
import logo from "../../../assets/images/logo.png"
import { Country, State } from "country-state-city";
import Context from "../../../context/Context";
import { scrollToError } from "../../../utils/helper";

const AuthForm = ({
    formType,
    onSubmit,
    initialValues = {},
    validationFunc,
    successRedirectPath,
    customLinks,
    buttonLabel,
    loadingLabel,
    customFields = [],
    isCustomSubmitFunction = false,
    customFunctionErrors = {},
    stepper = false,
    step = 1,
    children,
    title = "Hi, Welcome to Datara",
    subTitle = "Enter your credentials to access your account",
    mdSize,
    touched,
    setTouched
}) => {
    const [formValues, setFormValues] = useState(initialValues);
    const [errors, setErrors] = useState(
        isCustomSubmitFunction ? customFunctionErrors : {}
    );
    const [internalTouchedState, setInternalTouchedState] = useState({});
    const internalTouched = isCustomSubmitFunction ? touched : internalTouchedState;
    const internalSetTouched = isCustomSubmitFunction ? setTouched : setInternalTouchedState;
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // State for both phone number fields
    const [mobileCode, setMobileCode] = useState("+1");
    const [onboardingMobileCode, setOnboardingMobileCode] = useState("+1");
    const [onboardingManuallyChanged, setOnboardingManuallyChanged] = useState(false);

    const [states, setStates] = useState([]);
    const { refreshUser } = useContext(Context);
    const businessRoles = ['Company Head', 'Team Head', 'Member'];

    const navigate = useNavigate();

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        const finalValue = type === 'checkbox' ? checked : value;

        if (name === 'onboarding_contact') {
            setOnboardingManuallyChanged(true);
        }

        // 1. Update the form values
        const updatedValues = {
            ...formValues,
            [name]: finalValue,
        };
        setFormValues(updatedValues);

        // 2. Perform real-time validation
        if (validationFunc) {
            // We get the payload with country codes based on the "updatedValues"
            const payload = getPayloadWithCountryCodes(updatedValues);
            const { errors: validationErrors } = validationFunc(payload);

            setErrors(validationErrors);
        }

        // 3. Mark the field as touched immediately for real-time error display
        if (!internalTouched[name]) {
            internalSetTouched(prev => ({
                ...prev,
                [name]: true
            }));
        }
    };

    // Auto-populate onboarding_contact if not manually changed
    useEffect(() => {
        if (!onboardingManuallyChanged) {
            setFormValues(prev => ({
                ...prev,
                onboarding_contact: prev.phone_number
            }));
            setOnboardingMobileCode(mobileCode);
        }
    }, [formValues.phone_number, mobileCode, onboardingManuallyChanged]);


    const getPayloadWithCountryCodes = (currentValues) => {
        const payload = { ...currentValues };
        if (payload.phone_number) {
            payload.phone_number = `${mobileCode}${payload.phone_number}`;
        }
        if (payload.onboarding_contact) {
            payload.onboarding_contact = `${onboardingMobileCode}${payload.onboarding_contact}`;
        }
        return payload;
    };


    const handleBlur = (e) => {
        const { name } = e.target;
        // Keep internalSetTouched here just to ensure the field is marked 
        // if the user clicks in and out without typing
        internalSetTouched({
            ...internalTouched,
            [name]: true,
        });
    };

    useEffect(() => {
        if (isCustomSubmitFunction) {
            setErrors(customFunctionErrors);
        }
    }, [customFunctionErrors, isCustomSubmitFunction]);

    useEffect(() => {
        if (formValues?.country_region) {
            let countryName = formValues.country_region;

            if (countryName === "United States of America") {
                countryName = "United States";
            }

            const country = Country.getAllCountries().find(
                (c) => c.name === countryName
            );

            if (country) {
                const statesList = State.getStatesOfCountry(country.isoCode);
                setStates(statesList);
            } else {
                setStates([]);
            }
        }
    }, [formValues.country_region]);



    const handleSubmit = (event) => {
        event.preventDefault();

        const allTouched = customFields.reduce((acc, field) => {
            acc[field.name] = true;
            return acc;
        }, {});
        internalSetTouched(allTouched);

        setLoading(true);

        const payload = getPayloadWithCountryCodes(formValues);

        const { isValid, errors } = validationFunc(payload);
        if (isValid) {
            onSubmit(payload, (res) => {
                setLoading(false);
                setErrors({});
                if (res?.status === true || res?.status === 200) {
                    const loginMessage = res?.data?.access
                        ? `${res?.data?.role} logged in successfully`
                        : res?.message || res?.data?.message;

                    ReactNotifications(
                        loginMessage,
                        utilConstants.sucessState200
                    );
                    if (res?.data?.access) {
                        const decoded = jwtDecode(res?.data?.access);
                        const expirationTime = new Date(decoded?.exp * 1000);
                        Cookies.set('authToken', res?.data?.access, {
                            expires: expirationTime,
                            path: '/'
                        });
                        refreshUser();
                        localStorage.setItem('role', res?.data?.role);
                        if (businessRoles.includes(res?.data?.role)) return navigate('/dashboard');
                        if (res?.data?.role === 'Personal User') return navigate('/dashboard/shared');
                    } else {
                        sessionStorage.setItem('userEmail', formValues.email);
                        navigate(successRedirectPath);
                    }

                } else {
                    showApiErrors(res);
                }
            });
        } else {
            setErrors(errors);
            scrollToError(errors);
            setLoading(false);
        }
    };

    return (
        <div className="min-vh-100">
            <Container fluid>
                <Row className="min-vh-100">

                    <Col
                        md={4}
                        className="d-none d-md-flex align-items-center justify-content-center text-white p-[10px]"
                    >
                        <SignupLeftSideCard />
                    </Col>

                    <Col
                        xs={12}
                        md={8}
                        className="d-flex align-items-center justify-content-center"
                    >

                        <div className="w-100" style={{ maxWidth: "600px" }}>
                            <div className="flex items-center mb-6 block">
                                <img
                                    className="img-thumbnail !p-0 !border-none !max-w-[170px] md:!max-w-[180px]"
                                    src={logo}
                                    alt="logo"
                                />
                            </div>
                            {stepper && <div className="flex items-center mb-6 mt-2">
                                <Stepper currentStep={step} />
                            </div>}

                            <h3 className="text-2xl md:text-3xl lg:text-4xl font-medium mb-2">
                                {title}
                            </h3>
                            <p className="text-muted text-sm md:text-base mb-6">
                                {subTitle}
                            </p>
                            <Form
                                noValidate
                                className="shadow-lg shadow-gray-500/50 !rounded-none bg-white"
                                onSubmit={isCustomSubmitFunction ? (e) => onSubmit(e, getPayloadWithCountryCodes(formValues), mobileCode) : handleSubmit}
                            >
                                <div className="px-4 pb-4 ">
                                    <Row className=" max-h-[52vh] overflow-y-auto">
                                        {customFields?.map((field, index) => {
                                            const isTermsField = field?.name?.toLowerCase().includes("terms");

                                            // Determine which mobile code state to use
                                            const isPhoneNumber = field.name === 'phone_number';
                                            const isOnboardingContact = field.name === 'onboarding_contact';

                                            let currentMobileCode, currentSetMobileCode;
                                            if (isPhoneNumber) {
                                                currentMobileCode = mobileCode;
                                                currentSetMobileCode = setMobileCode;
                                            } else if (isOnboardingContact) {
                                                currentMobileCode = onboardingMobileCode;
                                                currentSetMobileCode = setOnboardingMobileCode;
                                            }

                                            return (
                                                <Col key={index} md={isTermsField ? 12 : mdSize ? mdSize : customFields.length > 5 ? 6 : 12} className="position-relative">
                                                    <div className="position-relative">
                                                        {field?.type === 'checkbox' ? (
                                                            <div className={`mt-4 d-flex align-items-center  ${isTermsField ? 'w-100 black-checkbox' : ''}`}>
                                                                <Form.Check
                                                                    label={field?.label}
                                                                    name={field?.name}
                                                                    type={field.type}
                                                                    id={`${field.type}-${index}`}
                                                                    checked={formValues[field.name]}
                                                                    onChange={onChange}
                                                                    onBlur={handleBlur}
                                                                    isInvalid={internalTouched[field.name] && errors[field.name]}
                                                                    className={`custom-dark-checkbox ${isTermsField ? 'w-100' : ''}`}
                                                                />
                                                            </div>
                                                        ) : field?.type !== 'select' ? (
                                                            <InputText
                                                                type={
                                                                    (field.name === "password" && showPassword) ||
                                                                        (field.name === "confirm_password" && showConfirmPassword)
                                                                        ? "text"
                                                                        : field.type
                                                                }
                                                                controlId={field.name}
                                                                name={field.name}
                                                                value={formValues?.[field.name] || ""}
                                                                error={internalTouched?.[field?.name] && errors?.[field?.name]}
                                                                placeholder={
                                                                    field.placeholder + (field.required ? "*" : "")
                                                                }
                                                                onChange={onChange}
                                                                onBlur={handleBlur}
                                                                mobileCode={currentMobileCode}
                                                                setMobileCode={currentSetMobileCode}
                                                                id={field?.id}
                                                                icon={field.name === "password"
                                                                    ? (showPassword ? <FaEyeSlash /> : <FaEye />)
                                                                    : field.name === "confirm_password"
                                                                        ? (showConfirmPassword ? <FaEyeSlash /> : <FaEye />)
                                                                        : null
                                                                }
                                                                onIconClick={
                                                                    field.name === "password"
                                                                        ? () => setShowPassword(!showPassword)
                                                                        : field.name === "confirm_password"
                                                                            ? () => setShowConfirmPassword(!showConfirmPassword)
                                                                            : null
                                                                }
                                                            />
                                                        ) : (
                                                            <>
                                                                <Form.Select
                                                                    aria-label="Default select example"
                                                                    name={field.name}
                                                                    placeholder={
                                                                        field.placeholder
                                                                            ? field.placeholder + (field.required ? "*" : "")
                                                                            : "Select" + (field.required ? "*" : "")
                                                                    }
                                                                    value={formValues[field?.name]}
                                                                    isInvalid={internalTouched[field.name] && errors[field.name]}
                                                                    onChange={onChange}
                                                                    onBlur={handleBlur}
                                                                    className="mt-4 custom-select"
                                                                >                                                                    {field.name === "state"
                                                                    ? (
                                                                        <>
                                                                            <option value="">Select State</option>
                                                                            {states.map((state) => (
                                                                                <option key={state.isoCode} value={state.name}>
                                                                                    {state.name}
                                                                                </option>
                                                                            ))}
                                                                        </>
                                                                    )
                                                                    : field.options &&
                                                                    field.options.map((option, idx) => (
                                                                        <option key={idx} value={option.value}>
                                                                            {option.label}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors[field.name]}
                                                                </Form.Control.Feedback>
                                                            </>
                                                        )}
                                                    </div>
                                                </Col>
                                            )
                                        })}
                                    </Row>

                                    <CustomButton className="w-100 login-btn mt-3 !bg-[#AEF468] !text-black !font-bold" type="submit" isLoading={loading}>
                                        {!loading ? buttonLabel : loadingLabel}
                                    </CustomButton>

                                    <div className="flex flex-col sm:flex-row gap-2 w-full">
                                        {customLinks &&
                                            customLinks.map((link, index) => (
                                                <Link key={index} to={link.to} className="w-full" onClick={link.onClick ? link.onClick : null}>
                                                    <CustomButton
                                                        variant={link?.variant}
                                                        className={`${link?.class ? link?.class : "w-full mt-3"} login-btn`}
                                                        type="button"
                                                    >
                                                        <span className="text-white text-decoration-none">
                                                            {link?.label}
                                                        </span>
                                                    </CustomButton>
                                                </Link>
                                            ))}
                                    </div>

                                </div>
                                {children && <div>{children}</div>}
                            </Form>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default AuthForm;
